import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getSecuritySettings, updateSecuritySettings } from '../api';
import useAuth from '../auth/useAuth';
import Button from './ui/Button';

const POLICIES = ['off', 'admins', 'all'];

/**
 * Who has to use two-factor authentication (server: two-factor-policy.service.ts).
 * Single sign-on accounts are never affected: their provider asks for theirs.
 */
function AdminSecuritySettings() {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [saved, setSaved] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    getSecuritySettings()
      .then(({ twoFactorPolicy }) => {
        setSaved(twoFactorPolicy);
        setPolicy(twoFactorPolicy);
      })
      .catch((loadError) => setError(loadError.message || t('adminPanel.errorGeneric')));
  }, [t]);

  async function save(event) {
    event.preventDefault();

    try {
      setIsSaving(true);
      setError('');
      setSuccess('');

      const { twoFactorPolicy } = await updateSecuritySettings(policy);
      setSaved(twoFactorPolicy);
      setSuccess(t('adminSecurity.saved'));
      // The policy may now apply to the administrator saving it.
      await refreshUser();
    } catch (saveError) {
      setError(saveError.message || t('adminPanel.errorGeneric'));
    } finally {
      setIsSaving(false);
    }
  }

  if (policy === null) {
    return error ? <p className="admin-user-section-hint">{error}</p> : null;
  }

  return (
    <form className="account-form admin-security" onSubmit={save} noValidate>
      <fieldset className="admin-security-policy">
        <legend>{t('adminSecurity.twoFactorLegend')}</legend>

        {POLICIES.map((value) => (
          <label key={value} className="admin-security-option">
            <input
              type="radio"
              name="twoFactorPolicy"
              value={value}
              checked={policy === value}
              disabled={isSaving}
              onChange={() => {
                setPolicy(value);
                setSuccess('');
              }}
            />
            <span>
              <strong>{t(`adminSecurity.policy.${value}`)}</strong>
              <small>{t(`adminSecurity.policyHint.${value}`)}</small>
            </span>
          </label>
        ))}
      </fieldset>

      <p className="admin-user-section-hint">{t('adminSecurity.ssoNote')}</p>

      {error && (
        <div className="login-message login-message-error" role="alert">
          <i className="fa-solid fa-circle-exclamation" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="login-message login-message-success" role="status">
          <i className="fa-solid fa-circle-check" />
          <span>{success}</span>
        </div>
      )}

      <div className="account-actions">
        <Button variant="primary" type="submit" disabled={isSaving || policy === saved}>
          {isSaving ? t('adminSecurity.saving') : t('adminSecurity.save')}
        </Button>
      </div>
    </form>
  );
}

export default AdminSecuritySettings;
